/**
 * Monitored NFT Collections Configuration
 *
 * ⚠️ FALLBACK DATA ONLY (2026-07-03) — collection→chain/displayName now resolve from
 * Firestore `tracked_collections` via the runtime registry below (setCollectionRegistry).
 * The static COLLECTION_METADATA map is consulted only when the registry hasn't been
 * hydrated yet (cold start) or is missing a slug. A chain migration is one Firestore
 * write to `tracked_collections/<slug>.blockchain` — no code deploy.
 *
 * Registry hydration points:
 * - Backend (Render): each service's loadTrackedCollections() (boot + reload-configs +
 *   AI-analysis cadence) merges its snapshot in via registryFromTrackedDocs().
 * - Frontend server: src/lib/collection-registry.ts (root layout, TTL-cached).
 * - Frontend client: CollectionRegistryHydrator in the root layout.
 *
 * OVERDUE CLEANUP (was Feb 2026) - Firestore configs should be stable by now.
 * Action items:
 * 1. Verify tracked_collections Firestore reads are reliable in production logs
 * 2. Retire FALLBACK_COLLECTIONS (now the single export below; both pollers import it)
 * 3. Watch for "[monitored-collections] static fallback won" warnings; when silent, the
 *    static map below can shrink to an empty cold-start shim.
 *
 * NOTE: Display names and other metadata can be overridden via admin dashboard (asset_metadata).
 * Images and descriptions come from OpenSea API and admin configuration.
 */

const COLLECTION_METADATA = {
  'pudgypenguins': {
    blockchain: 'ethereum',
    displayName: 'Pudgy Penguins',
  },
  'blupets': {
    blockchain: 'ethereum',
    displayName: 'Blupets',
  },
  'lilpudgys': {
    blockchain: 'ethereum',
    displayName: 'Lil Pudgys',
  },
  'zerotohero': {
    blockchain: 'abstract',
    displayName: 'ZeroToHero',
  },
  'gigaverse-roms-abstract': {
    blockchain: 'abstract',
    displayName: 'Gigaverse ROMs',
  },
  'gigaverse-giglings': {
    blockchain: 'abstract',
    displayName: 'Gigaverse Giglings',
  },
  'gigaverse-items': {
    blockchain: 'abstract',
    displayName: 'Gigaverse Items',
  },
  'glhfers': {
    blockchain: 'ethereum',
    displayName: 'GLHFers',
  },
  'bearish': {
    blockchain: 'abstract',
    displayName: 'Bearish',
  },
  // Abstract NFT collections intake (2026-06-11) — Category B asset wiring + Category C new entities.
  // These need explicit entries: getCollectionBlockchain falls back to 'unknown' (not 'abstract'),
  // so chain-scoped surfaces (/<chain>/market filter, blockchain badges) drop slugs missing here.
  'och-ringbearer': { blockchain: 'abstract', displayName: 'OCH Ringbearer' },
  'och-gacha-weapon': { blockchain: 'abstract', displayName: 'OCH WEAPON' },
  'och-items': { blockchain: 'abstract', displayName: 'OCH Items' },
  'ruyui': { blockchain: 'abstract', displayName: 'RUYUI' },
  'tollan-universe-genesis-avatars': { blockchain: 'abstract', displayName: 'Tollan Universe Genesis Avatars' },
  'dyli-abstract': { blockchain: 'abstract', displayName: 'DYLI' },
  'pengztracted-abstract': { blockchain: 'abstract', displayName: 'Pengztracted' },
  'lol-land-abstract': { blockchain: 'abstract', displayName: 'LOL Land' },
  'gachapoke': { blockchain: 'abstract', displayName: 'Gacha Pull' },
  'kabu-abstract': { blockchain: 'abstract', displayName: 'Kabu' },
  'hamieverse-genesis': { blockchain: 'abstract', displayName: 'Hamieverse Genesis' },
  'astroverseofficial': { blockchain: 'abstract', displayName: 'AstroVerse' },
  'buumeeofficial': { blockchain: 'abstract', displayName: 'BUUMEE' },
  'abstract-hotdogs-abstract': { blockchain: 'abstract', displayName: 'Abstract Hotdogs' },
  'multicycle-roaches': { blockchain: 'abstract', displayName: 'Multicycle Roaches' },
  'ultraman-archive78': { blockchain: 'abstract', displayName: 'Ultraman: Archive-78' },
  'meow-co0': { blockchain: 'abstract', displayName: 'Meow & Co' },
  'digitalinsects': { blockchain: 'abstract', displayName: 'Digital Insects' },
  'bigcoin-miners': {
    blockchain: 'abstract',
    displayName: 'Bitcoin Miners',
  },
  'genesishero-abstract': {
    blockchain: 'abstract',
    displayName: 'OCH Genesis Hero',
  },
  'fugzofficial': {
    blockchain: 'ethereum',
    displayName: 'Fugz',
  },
  'dreamiliomaker-abstract': {
    blockchain: 'abstract',
    displayName: 'Dreamilio Maker',
  },
  'abstractio': {
    blockchain: 'abstract',
    displayName: 'Abstractio',
  },
  'tengu-abs': {
    blockchain: 'abstract',
    displayName: 'Tengu',
  },
  'bigpool-abstract': {
    blockchain: 'abstract',
    displayName: 'Bigpool',
  },
  'finalbosu': {
    blockchain: 'ethereum',
    displayName: 'Final Bosu',
  },
  'cambriaislands': {
    blockchain: 'ethereum',
    displayName: 'Cambria Islands',
  },
  'cambriafounders': {
    blockchain: 'ethereum',
    displayName: 'Cambria Founders',
  },
  'cambriacores': {
    blockchain: 'ronin',
    displayName: 'Cambria Cores',
  },
  'cambria-companions': {
    blockchain: 'ronin',
    displayName: 'Cambria Companions',
  },
  'wolf-game': {
    blockchain: 'abstract',
    displayName: 'Wolf Game',
  },
  'wolf-game-valley-land': {
    blockchain: 'abstract',
    displayName: 'Wolf Game Valley Land',
  },
  'wolf-game-gen2': {
    blockchain: 'abstract',
    displayName: 'Wolf Game Gen 2',
  },
  'wolf-game-farmer': {
    blockchain: 'abstract',
    displayName: 'Wolf Game Farmer',
  },
  'wool-pouch': {
    blockchain: 'ethereum',
    displayName: 'Wool Pouch',
  },
  'canna-sapiens': {
    blockchain: 'abstract',
    displayName: 'Canna Sapiens',
  },
  // ── 2026-06-11 Abstract NFT-collections intake (16→35) ───────────────────────
  // Synced from Firestore `tracked_collections` (the source of truth) on 2026-06-12.
  // Without these entries `getCollectionBlockchain()` returned 'unknown' for the new
  // slugs, so the chain-lens filter on /abstract/market silently DROPPED them while
  // the unfiltered global /market still showed them. Keep this map in sync with
  // tracked_collections until chain resolution reads Firestore directly.
  'abstract-hotdogs-abstract': {
    blockchain: 'abstract',
    displayName: 'Abstract Hotdogs',
  },
  'astroverseofficial': {
    blockchain: 'abstract',
    displayName: 'Astroverse: SOVA',
  },
  'bearish': {
    // The collection's CURRENT OpenSea slug (the old 'bearish-abstract' entry above
    // is retained as legacy fallback only; its stats doc no longer exists).
    blockchain: 'abstract',
    displayName: 'Bearish',
  },
  'buumeeofficial': {
    blockchain: 'abstract',
    displayName: 'BUUMEE',
  },
  'digitalinsects': {
    blockchain: 'abstract',
    displayName: 'Digital Insects',
  },
  'dyli-abstract': {
    blockchain: 'abstract',
    displayName: 'DYLI',
  },
  'gachapoke': {
    blockchain: 'abstract',
    displayName: 'Gacha Pull',
  },
  'hamieverse-genesis': {
    blockchain: 'abstract',
    displayName: 'Hamieverse',
  },
  'kabu-abstract': {
    blockchain: 'abstract',
    displayName: 'Kabu',
  },
  'lol-land-abstract': {
    blockchain: 'abstract',
    displayName: 'LOL Land',
  },
  'meow-co0': {
    blockchain: 'abstract',
    displayName: 'Meow & Co',
  },
  'multicycle-roaches': {
    blockchain: 'abstract',
    displayName: 'Multicycle Roaches',
  },
  'och-gacha-weapon': {
    blockchain: 'abstract',
    displayName: 'OCH WEAPON',
  },
  'och-items': {
    blockchain: 'abstract',
    displayName: 'OCH Items',
  },
  'och-ringbearer': {
    blockchain: 'abstract',
    displayName: 'OCH Ringbearer',
  },
  'pengztracted-abstract': {
    blockchain: 'abstract',
    displayName: 'Pengztracted',
  },
  'ruyui': {
    blockchain: 'abstract',
    displayName: 'RUYUI',
  },
  'tollan-universe-genesis-avatars': {
    blockchain: 'abstract',
    displayName: 'Tollan Universe Genesis Avatars',
  },
  'ultraman-archive78': {
    blockchain: 'abstract',
    displayName: 'Ultraman: Archive-78',
  },
};

/**
 * Blockchain to Native Currency Mapping
 * Add new blockchains here when expanding to other networks
 */
const BLOCKCHAIN_CURRENCIES = {
  'ethereum': 'ETH',
  'abstract': 'ETH',  // Abstract uses ETH
  'ronin': 'RON',
  'base': 'ETH',      // Base uses ETH
  'solana': 'SOL',
  'polygon': 'MATIC',
  'arbitrum': 'ETH',
  'optimism': 'ETH',
  'avalanche': 'AVAX',
  'bsc': 'BNB',       // Binance Smart Chain
};

/**
 * List of all monitored collection slugs
 */
const MONITORED_COLLECTIONS = Object.keys(COLLECTION_METADATA);

/**
 * FALLBACK_COLLECTIONS — the cold-start poll list.
 *
 * SINGLE SOURCE OF TRUTH (2026-07-10, U13). Previously duplicated as byte-identical
 * literals in OpenSeaCollectionStatsService.js and NFTPollingService.js; both now
 * import this array. Used only when neither process.env.COLLECTIONS nor the Firestore
 * `tracked_collections` snapshot is available yet (boot before Firestore loads).
 *
 * NOTE: this is intentionally NOT `MONITORED_COLLECTIONS` — it is the curated poll set
 * the pollers ran with historically (e.g. it retains the legacy 'bearish-abstract' slug
 * and omits the 2026-06-11 intake additions present in COLLECTION_METADATA). Changing it
 * changes what the pollers fetch on a cold start, so treat edits as behavior changes.
 */
const FALLBACK_COLLECTIONS = [
  'pudgypenguins', 'lilpudgys', 'zerotohero', 'gigaverse-roms-abstract',
  'gigaverse-giglings', 'gigaverse-items', 'glhfers', 'bearish-abstract',
  'bigcoin-miners', 'genesishero-abstract', 'fugzofficial', 'dreamiliomaker-abstract',
  'abstractio', 'tengu-abs', 'bigpool-abstract', 'finalbosu', 'cambriaislands',
  'cambriafounders', 'cambriacores', 'cambria-companions', 'wolf-game',
  'wolf-game-valley-land', 'wolf-game-gen2', 'wolf-game-farmer', 'wool-pouch',
  'canna-sapiens', 'blupets'
];

// ── Live collection registry (tracked_collections mirror) ────────────────────
//
// Module-level so every getCollection* call site stays synchronous and untouched.
// Hydrated server-side from Firestore `tracked_collections` and client-side via
// the serialized map the root layout passes down. Null until first hydration —
// resolution then falls back to the static map (pre-registry behavior).
let COLLECTION_REGISTRY = null;
const fallbackWarned = new Set();

/**
 * Hydrate the registry. Entries need a string `blockchain`; anything else is
 * dropped. Empty/invalid maps are ignored so a failed Firestore read can never
 * wipe a previously-good registry. `merge: true` overlays onto the existing
 * registry (used by backend pollers whose snapshots are isActive-filtered).
 */
function setCollectionRegistry(map, options = {}) {
  if (!map || typeof map !== 'object') return;
  const entries = Object.entries(map).filter(
    ([, v]) => v && typeof v.blockchain === 'string' && v.blockchain
  );
  if (entries.length === 0) return;
  const next = options.merge && COLLECTION_REGISTRY ? { ...COLLECTION_REGISTRY } : {};
  for (const [slug, v] of entries) {
    next[slug] = {
      blockchain: v.blockchain.toLowerCase(),
      displayName: v.displayName || COLLECTION_METADATA[slug]?.displayName || slug,
    };
  }
  COLLECTION_REGISTRY = next;
}

/** Current registry (null until hydrated). Exposed for tests/diagnostics. */
function getCollectionRegistry() {
  return COLLECTION_REGISTRY;
}

/**
 * Build a registry map from Firestore `tracked_collections` snapshot docs
 * (anything with `.id` and `.data()`), so backend services can hydrate from
 * snapshots they already fetch — zero extra reads.
 */
function registryFromTrackedDocs(docs) {
  const map = {};
  for (const doc of docs || []) {
    const d = typeof doc.data === 'function' ? doc.data() : null;
    if (d && typeof d.blockchain === 'string' && d.blockchain) {
      map[doc.id] = { blockchain: d.blockchain, displayName: d.displayName };
    }
  }
  return map;
}

/**
 * Resolve a collection's metadata: live registry first, static map as fallback.
 * Logs (once per slug per process) when the registry exists but the static map
 * had to answer — that's drift between this file and tracked_collections.
 */
function resolveCollectionMeta(slug) {
  if (COLLECTION_REGISTRY) {
    const live = COLLECTION_REGISTRY[slug];
    if (live) return live;
    const fallback = COLLECTION_METADATA[slug];
    if (fallback && !fallbackWarned.has(slug)) {
      fallbackWarned.add(slug);
      console.warn(
        `[monitored-collections] '${slug}' missing from tracked_collections registry — static fallback won (chain=${fallback.blockchain})`
      );
    }
    return fallback || null;
  }
  return COLLECTION_METADATA[slug] || null;
}

/**
 * Get blockchain for a collection
 *
 * Live `tracked_collections` registry first; static map fallback. Returns
 * 'unknown' for collections in neither. Multi-chain (B1.5): we must NOT
 * silently coerce non-Abstract collections to 'abstract' — that would mislabel
 * Base/Ethereum/Ronin collections and let chain filters drop or misattribute them.
 */
function getCollectionBlockchain(slug) {
  return resolveCollectionMeta(slug)?.blockchain || 'unknown';
}

/**
 * Get native currency for a blockchain
 */
function getBlockchainCurrency(blockchain) {
  return BLOCKCHAIN_CURRENCIES[blockchain.toLowerCase()] || 'ETH';
}

/**
 * Get currency for a collection (convenience function)
 */
function getCollectionCurrency(slug) {
  const blockchain = getCollectionBlockchain(slug);
  return getBlockchainCurrency(blockchain);
}

/**
 * Get display name for a collection
 */
function getCollectionDisplayName(slug) {
  return resolveCollectionMeta(slug)?.displayName || slug;
}

/**
 * Get description for a collection
 * NOTE: Descriptions should come from OpenSea API or admin config. This is just a fallback.
 */
function getCollectionDescription(slug) {
  return `NFT collection: ${getCollectionDisplayName(slug)}`;
}

/**
 * Get image for a collection
 * NOTE: Images should come from OpenSea API or admin config. This always returns null.
 */
function getCollectionImage(slug) {
  return null; // Images fetched from OpenSea API or admin dashboard
}

/**
 * Get full metadata for a collection
 */
function getCollectionMetadata(slug) {
  return resolveCollectionMeta(slug) || {
    blockchain: 'abstract',
    displayName: slug,
  };
}

// CommonJS exports for Node.js
module.exports = {
  MONITORED_COLLECTIONS,
  FALLBACK_COLLECTIONS,
  COLLECTION_METADATA,
  BLOCKCHAIN_CURRENCIES,
  setCollectionRegistry,
  getCollectionRegistry,
  registryFromTrackedDocs,
  getCollectionBlockchain,
  getBlockchainCurrency,
  getCollectionCurrency,
  getCollectionDisplayName,
  getCollectionDescription,
  getCollectionImage,
  getCollectionMetadata,
};
